<template>
  <transition name="refresh-pill">
    <div
      v-if="isVisible"
      :class="['refresh-pill', { 'refresh-pill--bottom': isBottomPlacement }]"
      :style="pillStyle"
      role="status"
      aria-live="polite"
    >
      <span :class="['refresh-dot', refreshStatus]" />
      <span>{{ label }}</span>
    </div>
  </transition>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
  type CSSProperties,
} from 'vue'
import { useRoute } from 'vue-router'
import { useLoadingStore } from '@/stores/loading'
import { useAuthStore } from '@/stores/auth'

const loadingStore = useLoadingStore()
const authStore = useAuthStore()
const route = useRoute()

const pillPlacement = ref<'top' | 'bottom'>('top')
const pillOffset = ref(
  'calc(var(--balance-safe-area-top) + var(--balance-refresh-pill-gap))',
)

const refreshStatus = computed(() => loadingStore.refreshDataStatus)
const isVisible = computed(
  () => authStore.isLoggedIn && refreshStatus.value !== 'idle',
)
const isBottomPlacement = computed(() => pillPlacement.value === 'bottom')
const pillStyle = computed<CSSProperties>(() => {
  if (isBottomPlacement.value) {
    return {
      top: 'auto',
      bottom: pillOffset.value,
    }
  }

  return {
    top: pillOffset.value,
    bottom: 'auto',
  }
})

const label = computed(() => {
  if (refreshStatus.value === 'reauthenticating') return 'Reauthenticating'
  if (refreshStatus.value === 'success') return 'Data refreshed'
  if (refreshStatus.value === 'error') return 'Refresh issue'
  return 'Refreshing data'
})

const isMobileViewport = () => window.matchMedia('(max-width: 480px)').matches

const findVisibleElement = (selector: string) => {
  const elements = Array.from(document.querySelectorAll<HTMLElement>(selector))

  return elements.find((element) => {
    const page = element.closest('.ion-page')
    const rect = element.getBoundingClientRect()
    const style = window.getComputedStyle(element)

    return (
      !page?.classList.contains('ion-page-hidden') &&
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      rect.width > 0 &&
      rect.height > 0 &&
      rect.bottom > 0 &&
      rect.top < window.innerHeight
    )
  })
}

const setDefaultTopPlacement = () => {
  pillPlacement.value = 'top'
  pillOffset.value =
    'calc(var(--balance-safe-area-top) + var(--balance-refresh-pill-gap))'
}

const updatePillPosition = () => {
  if (typeof window === 'undefined') return

  const isMobile = isMobileViewport()

  if (isMobile && route.path.startsWith('/tabs/')) {
    const tabBar = findVisibleElement('ion-tab-bar')

    pillPlacement.value = 'bottom'
    if (!tabBar) {
      pillOffset.value =
        'calc(var(--balance-safe-area-bottom) + var(--balance-tab-bar-floating-offset))'
      return
    }

    const tabBarHeight = Math.max(
      0,
      window.innerHeight - tabBar.getBoundingClientRect().top,
    )
    pillOffset.value = `calc(${tabBarHeight}px + var(--balance-refresh-pill-gap))`
    return
  }

  if (isMobile) {
    const pageHeader = findVisibleElement('.page-header')

    if (pageHeader) {
      const headerBottom = Math.max(
        0,
        pageHeader.getBoundingClientRect().bottom,
      )
      pillPlacement.value = 'top'
      pillOffset.value = `calc(${headerBottom}px + var(--balance-refresh-pill-gap))`
      return
    }
  }

  setDefaultTopPlacement()
}

let animationFrame: number | null = null

const schedulePillPositionUpdate = () => {
  if (typeof window === 'undefined') return

  if (animationFrame !== null) {
    window.cancelAnimationFrame(animationFrame)
  }

  animationFrame = window.requestAnimationFrame(() => {
    animationFrame = null
    updatePillPosition()
  })
}

watch(
  () => route.fullPath,
  async () => {
    await nextTick()
    schedulePillPositionUpdate()
  },
)

watch(isVisible, async () => {
  await nextTick()
  schedulePillPositionUpdate()
})

onMounted(() => {
  schedulePillPositionUpdate()
  window.addEventListener('resize', schedulePillPositionUpdate)
  window.addEventListener('orientationchange', schedulePillPositionUpdate)
  window.addEventListener(
    'balance:native-safe-area-insets',
    schedulePillPositionUpdate,
  )
})

onUnmounted(() => {
  if (animationFrame !== null) {
    window.cancelAnimationFrame(animationFrame)
  }

  window.removeEventListener('resize', schedulePillPositionUpdate)
  window.removeEventListener('orientationchange', schedulePillPositionUpdate)
  window.removeEventListener(
    'balance:native-safe-area-insets',
    schedulePillPositionUpdate,
  )
})
</script>

<style scoped>
.refresh-pill {
  position: fixed;
  top: calc(var(--balance-safe-area-top) + var(--balance-refresh-pill-gap));
  left: 50%;
  z-index: 10000;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 138px;
  padding: 7px 12px;
  border: 1px solid var(--ion-color-gray3);
  border-radius: 999px;
  background: rgba(var(--ion-color-gray0-rgb), 0.96);
  color: var(--ion-text-color);
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  box-shadow: 0 8px 24px rgba(var(--ion-color-dark-rgb), 0.14);
  transform: translateX(-50%);
}

.refresh-dot {
  position: relative;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.refresh-dot::after {
  position: absolute;
  inset: -5px;
  border-radius: 50%;
  content: '';
}

.refresh-dot.refreshing,
.refresh-dot.reauthenticating {
  background: var(--ion-color-warning);
}

.refresh-dot.refreshing::after,
.refresh-dot.reauthenticating::after {
  background: rgba(var(--ion-color-warning-rgb), 0.35);
  animation: refresh-pulse 1.4s ease-out infinite;
}

.refresh-dot.success {
  background: var(--ion-color-success);
}

.refresh-dot.error {
  background: var(--ion-color-danger);
}

.refresh-dot.success::after,
.refresh-dot.error::after {
  display: none;
}

.refresh-pill-enter-active,
.refresh-pill-leave-active {
  transition:
    opacity 160ms ease,
    transform 160ms ease;
}

.refresh-pill-enter-from,
.refresh-pill-leave-to {
  opacity: 0;
  transform: translate(-50%, -8px);
}

.refresh-pill--bottom.refresh-pill-enter-from,
.refresh-pill--bottom.refresh-pill-leave-to {
  transform: translate(-50%, 8px);
}

@keyframes refresh-pulse {
  0% {
    opacity: 0.85;
    transform: scale(0.45);
  }

  100% {
    opacity: 0;
    transform: scale(1.4);
  }
}
</style>
